import Aldo from 'assets/svg/Aldo.svg';
import { useCallback, useState } from 'react';

import {
  isAlertDismissed,
  markAlertAsDismissed,
} from 'utils/AlertsStoreManager';
import { XIconOutline } from 'utils/icons';
import { Button } from '../Button';
import type { AlertProps } from './AlertV2.types';
import { AlertV2IconMap } from './AlertV2.types';
import { useAlertStyles } from './useAlertStyles';

/**
 * AlertV2 component
 * Figma Link: https://www.figma.com/design/WuWFjJRUJXAtDbaR0iTbvE/Design-System-%E2%9C%A6?node-id=4066-38159&t=GuRyYKWML7DSBXRT-0
 *
 * @param {AlertProps} props
 * @return {React.ReactNode}
 */
const AlertV2 = ({
  variant = 'filled',
  type,
  icon,
  renderIcon,
  title,
  description: subtitle,
  primaryAction,
  secondaryAction,
  dismissible,
  dismissId,
  onDismiss,
  size,
  className,
  actions,
  withAldo,
  extra,
}: AlertProps) => {
  const styles = useAlertStyles({ type, size, variant, className });
  const isSmall = size === 'x-small' || size === 'small';

  const [isDismissed, setIsDismissed] = useState(
    dismissId ? isAlertDismissed(dismissId) : false,
  );

  const handleDismiss = useCallback(() => {
    if (dismissId) {
      markAlertAsDismissed(dismissId);
    }
    setIsDismissed(true);
    onDismiss?.();
  }, [onDismiss, dismissId]);

  if (isDismissed) {
    return null;
  }

  const buttons = (
    <div className="flex gap-1">
      {/* primary action */}
      {primaryAction && (
        <Button
          asDefault
          className={styles.primaryAction}
          onClick={primaryAction.onClick}
          tracingEvent={primaryAction.tracingEvent}
        >
          {primaryAction.label}
        </Button>
      )}
      {secondaryAction && (
        <>
          {/* divider */}
          <span className={styles.divider}>∙</span>

          {/* secondary action */}
          <Button
            asDefault
            className={styles.secondaryAction}
            onClick={secondaryAction.onClick}
            tracingEvent={secondaryAction.tracingEvent}
          >
            {secondaryAction.label}
          </Button>
        </>
      )}
    </div>
  );

  const IconComponent = icon || AlertV2IconMap[type];

  return (
    <div className={styles.wrapper}>
      {/* icon */}
      {renderIcon ? (
        renderIcon(styles.icon)
      ) : (
        <IconComponent className={styles.icon} />
      )}

      {/* title */}
      <div className="flex-1 flex flex-col gap-1 self-center">
        {title && <h3 className={styles.title}>{title}</h3>}

        {/* subtitle */}
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

        {/* actions - if size is x-small or small, show actions in a new line */}
        {!isSmall && (actions || buttons)}
      </div>

      {/* actions - if size is large or large-mid, show actions on the right */}
      {isSmall && <div className="flex-shrink-0">{actions || buttons}</div>}

      {/* close Button */}
      {dismissible && (
        <Button
          onClick={handleDismiss}
          asDefault
          className={styles.closeButton}
        >
          <XIconOutline className="w-6 h-6" />
        </Button>
      )}
      {withAldo ? (
        <div className="absolute rounded-br-xl bottom-0 right-0 overflow-hidden">
          <img src={Aldo} alt="Aldo" className="h-16 -mb-2" />
        </div>
      ) : null}

      {extra}
    </div>
  );
};

export default AlertV2;
